// =======================================================
// Generator.js — base class for all visual generators
// =======================================================
//
// A Generator owns a set of three.js objects (meshes, points, shaders)
// that live in the engine's scene. It also declares a `params` schema
// which the UI uses to build sliders/color-pickers.
//
// Lifecycle:
//   onEnter(scene, engine)   — add meshes, compile shaders
//   onResize(w, h)           — update uniforms that depend on size
//   update(dt, time, engine) — animate per-frame
//   onExit(scene)            — remove meshes and dispose GPU resources
//   reseed()                 — randomize internal state (R key)
//
// Parameter schema (this.schema):
//   Object of { key: { default, min?, max?, step?, label?, type?, options? } }
//   type can be:
//     'range'  (default for numbers with min/max)
//     'color'  (hex string)
//     'bool'
//     'select' (requires options: [[value,label],...])

import * as THREE from 'three';

export class Generator {
  constructor({ name, category = 'intense', schema = {} } = {}) {
    this.name = name;
    this.category = category; // 'intense' | 'ambient'
    this.schema = schema;
    // this.values holds the live parameter values, initialised from defaults.
    this.values = {};
    for (const [k, def] of Object.entries(schema)) {
      this.values[k] = def.default;
    }
    this._disposables = [];
    this.seed = 1;
  }

  // Convenience: remember a three.js resource so onExit() can dispose it.
  track(obj) {
    this._disposables.push(obj);
    return obj;
  }

  onEnter(scene, engine) {
    // override
  }

  onExit(scene) {
    for (const obj of this._disposables) {
      if (obj.parent) obj.parent.remove(obj);
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) {
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        for (const m of mats) {
          if (m.map) m.map.dispose();
          m.dispose();
        }
      }
      if (obj.dispose && typeof obj.dispose === 'function' && !obj.material && !obj.geometry) {
        obj.dispose();
      }
    }
    this._disposables = [];
  }

  onResize(w, h) {
    // override
  }

  update(dt, time, engine) {
    // override
  }

  reseed() {
    // override to randomize
  }

  setSeed(seed) {
    this.seed = Math.max(1, (seed >>> 0) || 1);
  }

  rand01(index = 0) {
    let x = (this.seed ^ (index * 0x9e3779b9)) >>> 0;
    x ^= x >>> 16;
    x = Math.imul(x, 0x7feb352d) >>> 0;
    x ^= x >>> 15;
    x = Math.imul(x, 0x846ca68b) >>> 0;
    x ^= x >>> 16;
    // The XOR above produces a signed int32; coerce back to uint32 so the
    // division yields a proper [0, 1) value rather than [-0.5, 0.5).
    return (x >>> 0) / 4294967296;
  }

  randRange(index, min, max) {
    return min + (max - min) * this.rand01(index);
  }

  // read a param with a fallback
  v(key) { return this.values[key]; }

  setParam(key, value) {
    this.values[key] = value;
    if (this.onParamChange) this.onParamChange(key, value);
  }
}

// Shared GLSL snippets available to every generator shader.
// Keeping them here means every generator has the same toolbox.
export const GLSL_COMMON = /* glsl */ `
  // Pseudo-random per-pixel hash
  float hash11(float p) {
    p = fract(p * 0.1031);
    p *= p + 33.33;
    p *= p + p;
    return fract(p);
  }
  float hash21(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
  }
  vec2 hash22(vec2 p) {
    p = vec2(dot(p,vec2(127.1,311.7)), dot(p,vec2(269.5,183.3)));
    return fract(sin(p)*43758.5453);
  }

  // Value noise (cheap, good enough for warp/drift)
  float vnoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    float a = hash21(i);
    float b = hash21(i + vec2(1.0, 0.0));
    float c = hash21(i + vec2(0.0, 1.0));
    float d = hash21(i + vec2(1.0, 1.0));
    vec2 u = f*f*(3.0-2.0*f);
    return mix(a,b,u.x) + (c-a)*u.y*(1.0-u.x) + (d-b)*u.x*u.y;
  }

  // Fractal brownian motion (stacked noise)
  float fbm(vec2 p) {
    float v = 0.0, a = 0.5;
    for (int i = 0; i < 5; i++) {
      v += a * vnoise(p);
      p *= 2.0;
      a *= 0.5;
    }
    return v;
  }

  vec2 hash12(float p) {
    vec2 q = vec2(p, p + 1.2345);
    q = fract(q * vec2(123.34, 456.21));
    q += dot(q, q + 45.32);
    return fract(vec2(q.x * q.y, q.x + q.y));
  }

  // Signed distance to a horizontal line at y=c with thickness th
  float sdLineH(vec2 p, float c, float th) {
    return abs(p.y - c) - th;
  }

  // Hex -> vec3 (glsl can't do it at runtime so we pass color uniforms as vec3)
`;
